import { isDbRecordId } from '../../../db/odin-db-util';
import { useGetDbRecordByIdQuery } from '../../../redux/services/db-api';
import { useGetRecordByIdQuery } from '../../../redux/services/record';

/**
 * Retrieves a record by its ID, handling both online (server) and offline (local DB) data sources based on the record ID format and offline status.
 *
 * @param formId - The ID of the form associated with the record.
 * @param isRecordSaved - A boolean indicating if the record has been saved.
 * @param recordId - The unique identifier for the record (string for UUIDs or number for local IDs).
 * @param newRecordId - An optional ID for a newly created record if `recordId` is unavailable.
 * @returns An object containing the record data, fetching status, and error state from either the server or local database.
 */
const useGetRecord = (formId: number, isRecordSaved: boolean, recordId?: string, newRecordId?: number) => {
  const isDbRecord = isDbRecordId(recordId as string | number);

  const { data, isFetching, isError, error } = useGetRecordByIdQuery(
    {
      keyTypeId: true,
      id: recordId || newRecordId || 0,
      fetchLinkedRecords: true,
    },
    {
      skip:
        isDbRecord ||
        !isRecordSaved ||
        (recordId === undefined && newRecordId === undefined) ||
        formId === undefined ||
        formId <= 0,
    }
  );

  const {
    data: dbRecordData,
    isFetching: isDbRecordFetching,
    isError: hasDbRecordError,
    error: dbRecordError,
  } = useGetDbRecordByIdQuery(recordId as unknown as number, {
    skip: !isDbRecord || recordId === undefined,
  });

  return {
    data: isDbRecord ? dbRecordData : data,
    isFetching: isDbRecord ? isDbRecordFetching : isFetching,
    isError: isDbRecord ? hasDbRecordError : isError,
    error: isDbRecord ? dbRecordError : error,
  };
};

export default useGetRecord;
